'use client';

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BlockStack, Card, Spinner, Text } from '@shopify/polaris';
import { Image as ImageType, ImageProviders, MediaAssetType } from '@/types/media';
import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import { useHasCreatedSearchProducts } from '@/features/products/hooks/useHasCreatedSearchProducts';
import { Product } from '@/types/product';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import ProductFilter from '@/features/products/filters/ProductFilter';
import useProductSelect from '@/hooks/useProductSelect';

export type ProductImagesModalSectionProps = ImagesModalSectionProps;

export const ProductImagesModalSection = ({
  selectedMedia,
  product,
  setSelectedMedia,
  multiSelection = true,
}: ProductImagesModalSectionProps) => {
  const selectedUrls = useMemo(() => new Set(selectedMedia.map((image) => image.src)), [selectedMedia]);
  const { hasCreatedSearchProducts, isLoading: isLoadingHasCreatedSearchProducts } = useHasCreatedSearchProducts();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(product || null);

  const { options, loadProducts, isLoading: isLoadingProductSelect } = useProductSelect();

  useEffect(() => {
    if (product) {
      setSelectedProduct(product);
    }
  }, [product]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const productImages = useMemo(() => {
    if (!selectedProduct) {
      return options.flatMap((p) => p.images || []);
    } else {
      return selectedProduct.images || [];
    }
  }, [selectedProduct, options]);

  const addImage = useCallback(
    (image: ImageType) => {
      const newImage = {
        ...image,
        id: uuidv4(),
        provider: ImageProviders.product,
        type: MediaAssetType.image,
      };

      setSelectedMedia((prev) => {
        if (!multiSelection) {
          return [newImage];
        }
        return [...prev, newImage];
      });
    },
    [setSelectedMedia, multiSelection]
  );

  const removeImage = useCallback(
    (image: ImageType) => {
      setSelectedMedia((prev) => prev.filter((existing) => existing.src !== image.src));
    },
    [setSelectedMedia]
  );

  const onImageClick = useCallback(
    (image) => {
      const alreadySelected = selectedUrls.has(image.src!);
      if (alreadySelected) {
        return removeImage(image as ImageType);
      }
      return addImage(image as ImageType);
    },
    [addImage, removeImage, selectedUrls]
  );

  const items = useMemo(
    () =>
      (productImages || []).map((image) => ({
        ...image,
        type: MediaAssetType.image,
        alt: image.name || (selectedProduct ? selectedProduct.title : 'Product image'),
      })),
    [productImages, selectedProduct]
  );

  const renderItem = useCallback(
    (asset) => {
      return (
        <MediaAssetCard
          size="small"
          hideUseAction
          aspectRatio="portrait"
          media={asset}
          key={asset.id}
          selected={selectedUrls.has(asset.src)}
          onSelect={onImageClick}
        />
      );
    },
    [onImageClick, selectedUrls]
  );

  return (
    <BlockStack gap="400">
      <Text variant="headingMd" as="h2">
        Shopify
      </Text>
      {(hasCreatedSearchProducts || isLoadingHasCreatedSearchProducts) && (
        <Card>
          {isLoadingHasCreatedSearchProducts ? (
            <Spinner size="small" />
          ) : (
            <BlockStack gap="300">
              <ProductFilter selectedProduct={selectedProduct} onChange={setSelectedProduct} />
            </BlockStack>
          )}
        </Card>
      )}
      {items.length > 0 ? (
        <MediaGrid items={items} size="medium" renderItem={renderItem} />
      ) : (
        <Card>
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd" alignment="center">
              {isLoadingHasCreatedSearchProducts || isLoadingProductSelect ? (
                <Spinner size="small" />
              ) : (
                <>
                  {!hasCreatedSearchProducts
                    ? 'No product images found. Add products with images to your store.'
                    : selectedProduct
                      ? `No images found for ${selectedProduct.title}.`
                      : 'Please select a product to see its images.'}
                </>
              )}
            </Text>
          </BlockStack>
        </Card>
      )}
    </BlockStack>
  );
};
